import React, { useEffect, useState } from 'react';
import { getAllProducts } from '../../../../services/product';
import axios from 'axios';
import { message, Button, Switch, Input, Empty, Card, Row, Col, Tag } from 'antd';
import { isAuthenticated } from '../../../../services/auth';
import UnifiedDashboard from '../../../../components/layout/unified-dashboard';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { PlusOutlined, DeleteOutlined, SearchOutlined, BarsOutlined } from '@ant-design/icons';
import { Drawer, List, ListItem, ListItemText, ListItemIcon, Select, MenuItem, FormControl, InputLabel, TextField } from '@mui/material';
import { Search } from '@mui/icons-material';

const SponsoredAdsAdmin = () => {
  const [products, setProducts] = useState([]);
  const [sponsored, setSponsored] = useState([]);
  const [showDrawer, setShowDrawer] = useState(false);
  const [search, setSearch] = useState('');
  const [authorFilter, setAuthorFilter] = useState(null);
  const [loadingId, setLoadingId] = useState(null);

  const API = process.env.REACT_APP_API;
  const { token } = isAuthenticated();

  useEffect(() => {
    loadProducts();
    loadSponsored();
  }, []);

  const loadProducts = async () => {
    try {
      const res = await getAllProducts();
      setProducts(res.data);
    } catch (err) {
      message.error('Erro ao carregar produtos');
    }
  };

  const loadSponsored = async () => {
    try {
      const res = await axios.get(`${API}/admin/sponsored-ads`);
      setSponsored(res.data);
    } catch (err) {
      message.error('Erro ao carregar anúncios patrocinados');
    }
  };

  const handleAddSponsored = async (productId) => {
    setLoadingId(productId);
    try {
      const nextOrder = sponsored.length > 0 ? Math.max(...sponsored.map(s => s.order || 0)) + 1 : 1;
      await axios.post(
        `${API}/admin/sponsored-ads`,
        { productId, order: nextOrder },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadSponsored();
      await loadProducts();
      message.success('Produto adicionado ao destaque!');
      setShowDrawer(false);
      setSearch('');
      setAuthorFilter(null);
    } catch (err) {
      message.error(err.response?.data?.error || 'Erro ao adicionar destaque');
    }
    setLoadingId(null);
  };

  const handleRemoveSponsored = async (id) => {
    setLoadingId(`remove-${id}`);
    try {
      await axios.delete(`${API}/admin/sponsored-ads/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      message.success('Removido dos destaques!');
      await loadSponsored();
      await loadProducts();
    } catch (err) {
      message.error('Erro ao remover destaque');
    }
    setLoadingId(null);
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(sponsored);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const updatedItems = items.map((item, index) => ({ ...item, order: index + 1 }));
    setSponsored(updatedItems);

    const orderData = updatedItems.map((item) => ({ id: item._id, order: item.order }));
    try {
      await axios.put(
        `${API}/admin/sponsored-ads/reorder`,
        { order: orderData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadSponsored();
      message.success('Ordem atualizada!');
    } catch (err) {
      message.error('Erro ao atualizar ordem');
      await loadSponsored();
    }
  };

  const handleToggleActive = async (item) => {
    setLoadingId(`switch-${item._id}`);
    try {
      await axios.put(
        `${API}/admin/sponsored-ads/${item._id}`,
        { active: !item.active, order: item.order },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadSponsored();
      message.success(`Produto ${!item.active ? 'ativado' : 'desativado'}!`);
    } catch (err) {
      message.error('Erro ao atualizar status');
    } finally {
      setLoadingId(null);
    }
  };

  const availableProducts = products.filter(
    (p) => !sponsored.some((s) => s.product && s.product._id === p._id)
  );

  const filteredProducts = availableProducts.filter(p => {
    const matchName = search ? p.name.toLowerCase().includes(search.toLowerCase()) : true;
    const matchAuthor = authorFilter ? (p.author && p.author._id === authorFilter) : true;
    return matchName && matchAuthor;
  });

  const authors = React.useMemo(() => {
    const arr = Array.from(
      new Map(
        availableProducts
          .filter(p => p.author && p.author._id && p.author.name)
          .map(p => [p.author._id, { id: p.author._id, name: p.author.name }])
      ).values()
    );
    return arr.sort((a, b) => a.name.localeCompare(b.name));
  }, [availableProducts]);

  return (
    <UnifiedDashboard>
      <div className="sponsored-ads-content">
        {/* Header Section */}
        <div className="sponsored-ads-header">
          <div>
            <h1 className="sponsored-ads-title">
              Anúncios <span className="sponsored-ads-title-highlight">Patrocinados</span>
            </h1>
            <p className="sponsored-ads-description">Gerencie produtos em destaque e reordene por drag-and-drop</p>
          </div>
          <Button 
            type="primary" 
            size="large"
            icon={<PlusOutlined />}
            onClick={() => setShowDrawer(true)}
            className="sponsored-ads-add-btn"
          >
            Adicionar Destaque
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="sponsored-ads-stats">
          <Card className="stat-card">
            <div className="stat-content">
              <p className="stat-value">{sponsored.length}</p>
              <p className="stat-label">Em Destaque</p>
            </div>
          </Card>
          <Card className="stat-card">
            <div className="stat-content">
              <p className="stat-value">{sponsored.filter(s => s.active).length}</p>
              <p className="stat-label">Ativos</p>
            </div>
          </Card>
          <Card className="stat-card">
            <div className="stat-content">
              <p className="stat-value">{availableProducts.length}</p>
              <p className="stat-label">Disponíveis</p>
            </div>
          </Card>
        </div>

        {/* MUI Drawer for selecting products */}
        <Drawer
          anchor="right"
          open={showDrawer}
          onClose={() => setShowDrawer(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: { xs: '100vw', sm: 420 },
              maxWidth: { xs: '100vw', sm: 420 },
              zIndex: 3000,
            },
          }}
        >
          <div className="sponsored-ads-drawer-header">
            <h2 className="sponsored-ads-drawer-title">Selecionar Produto</h2>
            <Button 
              type="text" 
              onClick={() => setShowDrawer(false)} 
              className="sponsored-ads-close-btn"
            >
              <span className="sponsored-ads-close-icon">✕</span>
            </Button>
          </div>
          
          <div className="sponsored-ads-drawer-body">
            <TextField
              placeholder="Buscar produto..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: <Search />,
              }}
              className="sponsored-ads-search"
            />
            
            <FormControl fullWidth className="sponsored-ads-filter">
              <InputLabel>Filtrar por autor</InputLabel>
              <Select
                value={authorFilter || ''}
                onChange={(e) => setAuthorFilter(e.target.value || null)}
                label="Filtrar por autor"
                MenuProps={{ style: { zIndex: 3200 } }}
              >
                <MenuItem value="">
                  <em>Todos</em>
                </MenuItem>
                {authors.map(a => (
                  <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {filteredProducts.length === 0 ? (
              <Empty description="Nenhum produto disponível" />
            ) : (
              filteredProducts.map(item => (
                <Card key={item._id} className="sponsored-ads-list-item">
                  <Row align="middle" gutter={[12, 0]}>
                    <Col span={5}>
                      <img
                        src={item.images?.[0] ? `${API.replace('/api', '')}${item.images[0]}` : undefined}
                        alt={item.name}
                        style={{ width: '56px', height: '56px', objectFit: 'cover', borderRadius: '4px' }}
                      />
                    </Col>
                    <Col span={13}>
                      <h4 className="sponsored-ads-product-title">{item.name}</h4>
                      <p className="sponsored-ads-product-author">Por: {item.author?.name || 'Desconhecido'}</p>
                    </Col>
                    <Col span={6} style={{ textAlign: 'right' }}>
                      <Button
                        type="primary"
                        size="small"
                        loading={loadingId === item._id}
                        onClick={() => handleAddSponsored(item._id)}
                        icon={<PlusOutlined />}
                      >
                        Destacar
                      </Button>
                    </Col>
                  </Row>
                </Card>
              ))
            )}
          </div>
        </Drawer>

        {/* Sponsored List */}
        <Card className="sponsored-list-card">
          {sponsored.length === 0 ? (
            <Empty 
              description="Nenhum anúncio patrocinado" 
              style={{ paddingTop: '40px', paddingBottom: '40px' }}
            />
          ) : (
            <>
              <div className="sponsored-ads-alert">
                📍 Arraste pela alça para reordenar
              </div>
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="sponsored-list">
                  {(provided, snapshot) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className={`sponsored-ads-list ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}
                    >
                      {sponsored.map((item, index) => (
                        <Draggable key={item._id} draggableId={item._id} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`sponsored-ads-item ${snapshot.isDragging ? 'dragging' : ''} ${!item.active ? 'inactive' : ''}`}
                            >
                              <div 
                                className="sponsored-ads-item-drag-handle" 
                                {...provided.dragHandleProps}
                              >
                                <BarsOutlined />
                              </div>

                              <img
                                src={item.product?.images?.[0] ? `${API.replace('/api', '')}${item.product.images[0]}` : undefined}
                                alt={item.product?.name || 'Produto'}
                                className="sponsored-ads-item-avatar"
                                style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '4px' }}
                              />

                              <div className="sponsored-ads-item-info">
                                <h4 className="sponsored-ads-item-name">
                                  {item.product?.name || 'Produto removido'}
                                </h4>
                                <p className="sponsored-ads-item-desc">
                                  {item.product?.author?.name || 'Desconhecido'}
                                </p>
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                  <Tag color="blue">Posição #{item.order}</Tag>
                                  <Tag color={item.active ? 'green' : 'red'}>
                                    {item.active ? 'Ativo' : 'Inativo'}
                                  </Tag>
                                </div>
                              </div>

                              <div className="sponsored-ads-item-actions">
                                <Switch
                                  checked={item.active}
                                  loading={loadingId === `switch-${item._id}`}
                                  onChange={() => handleToggleActive(item)}
                                />
                                <Button
                                  type="primary"
                                  danger
                                  size="small"
                                  icon={<DeleteOutlined />}
                                  loading={loadingId === `remove-${item._id}`}
                                  onClick={() => handleRemoveSponsored(item._id)}
                                  className="sponsored-ads-remove-btn"
                                >
                                  Remover
                                </Button>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </>
          )}
        </Card>
      </div>
    </UnifiedDashboard>
  );
};

export default SponsoredAdsAdmin;
